import React from 'react';
import { PassData, formatDate, passTypeLabel } from './types';

// 2×2 on mobile, 1×4 on desktop. Values are real pass metadata only —
// tiles with no backing data are hidden rather than filled with mock copy.

const TileIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="w-10 h-10 rounded-xl bg-[#0A3B2C] text-[#AEDD1F] flex items-center justify-center shrink-0">
    {children}
  </span>
);

interface Tile {
  key: string;
  label: string;
  value: string;
  icon: React.ReactNode;
}

const icon = {
  org: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" />
    </svg>
  ),
  type: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 12l-8.5 8.5a2.1 2.1 0 01-3 0L3 15V3h12l5 5a2.1 2.1 0 010 3z" />
      <circle cx="7.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  views: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  updated: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  expiry: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  ),
};

const InfoTiles: React.FC<{ passData: PassData }> = ({ passData }) => {
  const tiles: Tile[] = [];

  if (passData.organisation_name) {
    tiles.push({ key: 'org', label: 'Organization', value: passData.organisation_name, icon: icon.org });
  }
  tiles.push({ key: 'type', label: 'Pass type', value: passTypeLabel(passData.pass_type), icon: icon.type });

  // "clicks" is the real per-keyword view counter walletHandler increments on
  // each visit. Neither platform reports install counts back per keyword, so
  // this is the honest number we have — labelled as views, not adds.
  if (typeof passData.clicks === 'number' && passData.clicks > 0) {
    tiles.push({
      key: 'views',
      label: 'Pass viewed',
      value: `${passData.clicks.toLocaleString()} ${passData.clicks === 1 ? 'time' : 'times'}`,
      icon: icon.views,
    });
  }

  const updated = formatDate(passData.last_modified || passData.created_at);
  if (updated) {
    tiles.push({ key: 'updated', label: 'Last updated', value: updated, icon: icon.updated });
  }

  const expiry = formatDate(passData.expiration_date);
  if (tiles.length < 4 && expiry) {
    tiles.push({ key: 'expiry', label: 'Valid until', value: expiry, icon: icon.expiry });
  }

  if (tiles.length === 0) return null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {tiles.slice(0, 4).map((t) => (
        <div key={t.key} className="bg-white rounded-2xl border border-[#E4EAE2] px-4 py-4 flex flex-col items-start gap-2.5">
          <TileIcon>{t.icon}</TileIcon>
          <div className="min-w-0">
            <p className="text-xs text-gray-500">{t.label}</p>
            <p className="text-sm font-semibold text-gray-900 break-words">{t.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default InfoTiles;
